<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { ChannelFilter, InboxTab, Scope } from '../../api/types'
import { CHANNEL_FILTERS, clockTime, GROUP_LABEL, SCOPE_AI_NOTE, scopeApplies, scopeFor, SORT_LABEL, TAB_LABEL, TABS, type RowGroup } from '../../lib/inbox'
import { AUTO_OFF_TITLE, autoOffReason } from '../../lib/activity'
import { useAiMode } from '../../stores/aimode'
import { useClock } from '../../stores/clock'
import { useInbox } from '../../stores/inbox'
import { useSession } from '../../stores/session'
import Icon from '../ui/Icon.vue'
import ToggleSwitch from '../ui/ToggleSwitch.vue'
import TagFilterChip from '../collab/TagFilterChip.vue'
import TagFilterOptions from '../collab/TagFilterOptions.vue'
import ConversationRow from './ConversationRow.vue'
import TeamLoad from './TeamLoad.vue'

const store = useInbox()
const clock = useClock()
const router = useRouter()

// the full hint does not fit the narrower list on 1280-wide screens
const narrowMq = window.matchMedia('(max-width: 1399px)')
const narrow = ref(narrowMq.matches)
const onMq = (e: MediaQueryListEvent) => (narrow.value = e.matches)
narrowMq.addEventListener('change', onMq)
onBeforeUnmount(() => narrowMq.removeEventListener('change', onMq))
const placeholder = computed(() => (narrow.value ? 'ค้นหาชื่อ เบอร์ ข้อความ' : 'ค้นหาชื่อ เบอร์ หรือข้อความ'))

const loadText = computed(() => (store.me ? `ดูแลอยู่ ${store.me.open_count} จาก ${store.me.cap} แชท` : 'กำลังโหลด…'))

// พร้อมรับ switched itself off after no activity (docs/routing.md): a calm note + one tap to turn it back on
const autoOff = computed(() => {
  const a = store.autoOffNotice
  if (!a) return null
  return { reason: autoOffReason(a.afterMin), when: a.at ? `เมื่อ ${clockTime(a.at)} น.` : null }
})
const availTip = computed(() => {
  const m = store.settings.auto_off_min ?? 0
  const base = store.available ? 'พร้อมรับแชทใหม่ · กดเพื่อพัก' : 'พักอยู่ · กดเพื่อพร้อมรับแชทใหม่'
  return m > 0 ? `${base} · ปิดเองเมื่อไม่มีการใช้งาน ${m} นาที` : base
})

// ---- search (debounced)
const q = ref('')
let t: ReturnType<typeof setTimeout> | null = null
function onSearch(): void {
  if (t) clearTimeout(t)
  t = setTimeout(() => void store.runSearch(q.value), 250)
}
function clearSearch(): void {
  q.value = ''
  store.clearSearch()
}
onBeforeUnmount(() => t && clearTimeout(t))
const searchActive = computed(() => store.searchHits !== null)

// ---- channel dropdown
const ddOpen = ref(false)
const channelLabel = computed(() => CHANNEL_FILTERS.find((c) => c.value === store.channel)?.label ?? 'ทุกช่องทาง')
function pickChannel(v: ChannelFilter): void {
  ddOpen.value = false
  store.setChannel(v)
}
function onDdKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') ddOpen.value = false
}

const scopes: { value: Scope; label: string }[] = [
  { value: 'team', label: 'ทั้งทีม' },
  { value: 'mine', label: 'ของฉัน' },
]
// the AI tab always lists the whole team's AI chats (owner 27 ก.ย. 69): the control shows ทั้งทีม, dimmed, and says why
const scopeOff = computed(() => !scopeApplies(store.tab) && !searchActive.value)
const shownScope = computed(() => (scopeOff.value ? scopeFor(store.tab, store.scope) : store.scope))
function pickScope(v: Scope): void {
  if (scopeOff.value) store.flash(SCOPE_AI_NOTE) // phones have no hover title: say it on a tap
  else store.setScope(v)
}

// กำลังคุย's number = chats waiting for OUR reply (green pill, like ต้องตอบ before) · AI / ทักตาม: a quiet number
const TAB_COUNT: Record<InboxTab, () => number | null> = {
  active: () => store.counts.needs_reply,
  ai: () => store.counts.ai ?? 0,
  follow_up: () => store.counts.follow_up,
  closed: () => null,
}
const TAB_TIP: Record<InboxTab, (n: number | null) => string> = {
  active: (n) => `กำลังคุย · ต้องตอบ ${n ?? 0} แชท · รวมแชทที่รอลูกค้าตอบ`,
  ai: (n) => `AI กำลังดูแล ${n ?? 0} แชท · ของทั้งทีม`,
  follow_up: (n) => `ถึงเวลาทักตาม ${n ?? 0} ราย`,
  closed: () => 'แชทที่ปิดแล้ว',
}
const tabs = computed(() =>
  TABS.map((tab: InboxTab) => {
    const n = TAB_COUNT[tab]()
    return { tab, label: TAB_LABEL[tab], n, tip: TAB_TIP[tab](n), on: store.tab === tab && !searchActive.value }
  }),
)

// tablist keys (WAI-ARIA): ← → move between the tabs, Home / End jump to the first / last
const tabEls = ref<HTMLButtonElement[]>([])
function onTabKey(e: KeyboardEvent, i: number): void {
  const last = TABS.length - 1
  const to = e.key === 'ArrowRight' ? (i === last ? 0 : i + 1) : e.key === 'ArrowLeft' ? (i === 0 ? last : i - 1)
    : e.key === 'Home' ? 0 : e.key === 'End' ? last : -1
  if (to < 0) return
  e.preventDefault()
  store.setTab(TABS[to]!)
  tabEls.value[to]?.focus()
}

const emptyText: Record<InboxTab, string> = {
  active: 'ตอบครบแล้ว ยังไม่มีแชทที่กำลังคุยอยู่',
  ai: 'ตอนนี้ AI ไม่ได้ดูแลแชทไหนอยู่',
  follow_up: 'ยังไม่มีลูกค้าที่ถึงเวลาทักตาม',
  closed: 'ยังไม่มีแชทที่ปิดแล้ว',
}

// ---- กำลังคุย: ต้องตอบ, then a quiet header for รอลูกค้าตอบ (numbers = the server's, like the tab badge)
function groupCount(g: RowGroup): number {
  return g.key === 'waiting' ? store.counts.waiting : store.counts.needs_reply
}
/** The ต้องตอบ header is shown whenever the tab has rows (empty → "ตอบครบแล้ว"); รอลูกค้าตอบ only with rows. */
function showHead(g: RowGroup): boolean {
  return !!g.key && store.rows.length > 0 && (g.key === 'needs_reply' || g.rows.length > 0)
}

// ---- AI tab
const aimode = useAiMode()
const session = useSession()
/** The owner switched "AI ดูแลแชท" off (bootstrap): the empty tab says so instead of "not in any chat now". */
const aiOff = computed(() => store.aiModeEnabled === false)
const aiLink = computed(() =>
  session.me?.role === 'owner' ? 'เปิดได้ที่ ตั้งค่า › AI' : session.me?.role === 'head' ? 'ดูที่ ตั้งค่า › AI · เจ้าของเป็นผู้เปิด' : null,
)
async function takeOver(id: string): Promise<void> {
  // the chat opens first (route → select), so its row stays in place here with "AI หยุดแล้ว · อยู่ใน…" (stores/inbox.ts)
  await router.push({ name: 'conversation', params: { id } })
  await nextTick()
  await aimode.takeOver(id)
}

function open(id: string): void {
  void router.push({ name: 'conversation', params: { id } })
}

function onRowsScroll(e: Event): void {
  const el = e.target as HTMLElement
  if (el.scrollTop + el.clientHeight > el.scrollHeight - 200) void store.loadMore()
}
</script>

<template>
  <section class="list" aria-label="รายการแชท">
    <header class="lh">
      <div class="lh-top">
        <div class="lh-tt">
          <h1 class="lh-title">แชท</h1>
          <p class="lh-sub">{{ loadText }}</p>
        </div>
        <button
          class="avail"
          :class="{ off: !store.available }"
          role="switch"
          :aria-checked="store.available"
          aria-label="พร้อมรับแชทใหม่"
          :title="availTip"
          @click="store.setAvailable(!store.available)"
        >
          <span>{{ store.available ? 'พร้อมรับ' : 'พักอยู่' }}</span>
          <ToggleSwitch :on="store.available" size="md" />
        </button>
      </div>

      <div v-if="autoOff" class="aon" role="status">
        <span class="aon-ic"><Icon name="pauseCircle" /></span>
        <div class="aon-b">
          <p class="aon-t">{{ AUTO_OFF_TITLE }}</p>
          <p class="aon-r">{{ autoOff.reason }}</p>
          <p class="aon-s">{{ autoOff.when ? `${autoOff.when} · ` : '' }}ระหว่างนี้ไม่แจกแชทใหม่ให้</p>
          <div class="aon-a">
            <button type="button" class="aon-on" @click="store.setAvailable(true)">เปิดพร้อมรับ</button>
            <button type="button" class="aon-x" @click="store.dismissAutoOff()">ปิดข้อความนี้</button>
          </div>
        </div>
      </div>

      <div class="lh-find">
        <label class="search">
          <Icon name="search" />
          <input v-model="q" type="search" aria-label="ค้นหาแชท" :placeholder="placeholder" @input="onSearch" @keydown.esc="clearSearch" />
          <button v-if="q" class="clr" aria-label="ล้างคำค้น" @click="clearSearch"><Icon name="x" /></button>
        </label>
        <div class="dd-wrap" @keydown="onDdKey">
          <button
            class="dd"
            aria-haspopup="listbox"
            :aria-expanded="ddOpen"
            :aria-label="`กรองช่องทาง ตอนนี้แสดง${channelLabel}`"
            @click="ddOpen = !ddOpen"
          >
            <span>{{ channelLabel }}</span><Icon name="chevronDown" />
          </button>
          <div v-if="ddOpen" class="dd-back" @click="ddOpen = false"></div>
          <ul v-if="ddOpen" class="dd-menu" role="listbox" aria-label="ช่องทาง">
            <li v-for="c in CHANNEL_FILTERS" :key="c.value">
              <button role="option" :aria-selected="store.channel === c.value" class="dd-opt" @click="pickChannel(c.value)">
                <span>{{ c.label }}</span>
                <Icon v-if="store.channel === c.value" name="check" />
              </button>
            </li>
            <TagFilterOptions @picked="ddOpen = false" />
          </ul>
        </div>
      </div>

      <div class="lh-opts">
        <div
          class="scope"
          :class="{ 'is-off': scopeOff }"
          role="group"
          :aria-label="scopeOff ? `ขอบเขตแชท · ${SCOPE_AI_NOTE}` : 'ขอบเขตแชท'"
          :title="scopeOff ? SCOPE_AI_NOTE : undefined"
        >
          <button
            v-for="s in scopes"
            :key="s.value"
            class="sc-b"
            :class="{ 'is-on': shownScope === s.value }"
            :aria-pressed="shownScope === s.value"
            :aria-disabled="scopeOff || undefined"
            :title="scopeOff ? SCOPE_AI_NOTE : undefined"
            @click="pickScope(s.value)"
          >
            {{ s.label }}
          </button>
        </div>
        <button class="sort" :aria-label="`เรียงตาม: ${SORT_LABEL[store.sortModes[store.tab]]}`" title="สลับการเรียง" @click="store.toggleSort()">
          <Icon name="sort" /><span>{{ SORT_LABEL[store.sortModes[store.tab]] }}</span>
        </button>
      </div>
      <TagFilterChip />

      <div class="tabs" role="tablist" aria-label="สถานะแชท">
        <button
          v-for="(x, i) in tabs"
          :key="x.tab"
          ref="tabEls"
          class="tab"
          :class="{ 'is-on': x.on, 'tab-ai': x.tab === 'ai' }"
          role="tab"
          :aria-selected="x.on"
          :aria-label="x.tip"
          :title="x.tip"
          @click="store.setTab(x.tab)"
          @keydown="onTabKey($event, i)"
        >
          <Icon v-if="x.tab === 'ai'" name="sparkle" />
          <span>{{ x.label }}</span>
          <span v-if="x.tab === 'active' && x.n" class="tab-b">{{ x.n }}</span>
          <span v-else-if="x.tab !== 'active' && x.n" class="tab-n">{{ x.n }}</span>
        </button>
      </div>
    </header>

    <div class="rows scroll" @scroll.passive="onRowsScroll">
      <template v-if="searchActive">
        <p class="rows-note">{{ store.searching ? 'กำลังค้นหา…' : `ผลค้นหา "${store.query}" ${store.searchHits?.length ?? 0} แชท` }}</p>
        <p v-if="!store.searching && store.searchPartial" class="rows-note">ค้นในแชทเก่าไม่ทัน ผลนี้มาจากแชทปัจจุบัน ลองพิมพ์คำให้เจาะจงขึ้น</p>
        <ConversationRow
          v-for="h in store.searchHits ?? []"
          :key="'s' + h.conversation.id"
          :conv="store.convs[h.conversation.id] ?? h.conversation"
          :tab="null"
          :now="clock.now"
          :active="store.selectedId === h.conversation.id"
          :settings="store.settings"
          :staff="store.staffInfo(h.conversation.assigned_staff?.id)"
          :snippet="h.message?.text ?? null"
          @open="open"
        />
      </template>
      <template v-else>
        <template v-for="g in store.groups" :key="g.key ?? 'rows'">
          <h2 v-if="showHead(g)" class="grp" :class="'grp-' + g.key">
            <span class="grp-t">{{ GROUP_LABEL[g.key!] }}</span>
            <span v-if="g.key === 'needs_reply' && g.rows.length === 0" class="grp-done"><Icon name="check" />ตอบครบแล้ว</span>
            <span v-else class="grp-n">{{ groupCount(g) }}</span>
          </h2>
          <template v-for="c in g.rows" :key="c.id">
            <!-- AI tab: "รับเรื่องเอง" next to the row (a button cannot sit inside the row's button) -->
            <div v-if="store.tab === 'ai'" class="ai-rw">
              <ConversationRow
                :conv="c"
                :tab="store.tab"
                :now="clock.now"
                :active="store.selectedId === c.id"
                :settings="store.settings"
                :staff="store.staffInfo(c.assigned_staff?.id)"
                :moved="store.stickyNote(c.id)"
                :action="!!c.ai_mode"
                @open="open"
              />
              <button
                v-if="c.ai_mode && !store.stickyNote(c.id)"
                type="button"
                class="take"
                :disabled="!!aimode.busy[c.id]"
                :aria-label="`รับเรื่องเอง: หยุด AI แล้วรับแชทของ ${c.contact.name} มาตอบเอง`"
                title="หยุด AI แล้วรับแชทนี้มาตอบเอง"
                @click="takeOver(c.id)"
              >
                <Icon name="handoff" />รับเรื่องเอง
              </button>
            </div>
            <ConversationRow
              v-else
              :conv="c"
              :tab="store.tab"
              :now="clock.now"
              :active="store.selectedId === c.id"
              :settings="store.settings"
              :staff="store.staffInfo(c.assigned_staff?.id)"
              :moved="store.stickyNote(c.id)"
              :quiet="g.key === 'waiting'"
              @open="open"
            />
          </template>
        </template>
        <div v-if="store.rows.length === 0 && !store.listLoading && store.tab === 'ai' && !store.listError" class="empty ai-empty">
          <span class="ai-em-ic"><Icon name="sparkles" /></span>
          <p class="em-t">{{ aiOff ? 'ตอนนี้ยังไม่ได้เปิดให้ AI ตอบแชท' : emptyText.ai }}</p>
          <p class="em-s">
            {{ aiOff ? 'เมื่อเปิดโหมด AI ดูแลแชทแล้ว แชทที่ AI กำลังตอบจะขึ้นที่นี่' : 'กด “ให้ AI ดูแล” ที่หัวแชท แล้วแชทนั้นจะมาอยู่ที่นี่' }}
          </p>
          <RouterLink v-if="aiOff && aiLink" class="em-link" :to="{ name: 'config', params: { section: 'ai' } }"
            >{{ aiLink }}<Icon name="chevronRight"
          /></RouterLink>
        </div>
        <div v-else-if="store.rows.length === 0 && !store.listLoading" class="empty">
          <Icon name="inbox" />
          <p>{{ store.listError ? `โหลดรายการไม่สำเร็จ: ${store.listError}` : emptyText[store.tab] }}</p>
          <button v-if="store.listError" class="btn" @click="store.fetchList()">ลองใหม่</button>
        </div>
      </template>
    </div>

    <TeamLoad />
  </section>
</template>

<style scoped>
.list {
  width: clamp(320px, 25vw, 360px);
  flex: none;
  background: var(--sf);
  border-right: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.lh {
  flex: none;
  padding: 16px 16px 0;
  border-bottom: 1px solid var(--line);
}
.lh-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.lh-tt {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.lh-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 30px;
  letter-spacing: -0.2px;
}
.lh-sub {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
.avail {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 6px 0 12px;
  border: 1px solid #cfe3d8;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  background: var(--acs);
  color: var(--ac2);
}
.avail.off {
  background: #f4f6f5;
  border-color: var(--line);
  color: var(--mut);
}
/* พร้อมรับ switched off after no activity: calm, not an alarm */
.aon {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #f7f9f8;
}
.aon-ic {
  flex: none;
  display: inline-flex;
  margin-top: 1px;
  color: var(--mut);
}
.aon-ic :deep(.ic) {
  width: 18px;
  height: 18px;
}
.aon-b {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.aon-t {
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--ink);
}
.aon-r {
  font-size: 13px;
  line-height: 20px;
  color: #2e3b35;
}
.aon-s {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.aon-a {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
}
.aon-on {
  height: 28px;
  padding: 0 12px;
  border-radius: 8px;
  background: var(--ac);
  color: #fff;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}
.aon-on:hover {
  background: var(--ac2);
}
.aon-x {
  height: 28px;
  padding: 0 4px;
  border-radius: 6px;
  font-size: 12.5px;
  line-height: 20px;
  color: var(--mut);
  white-space: nowrap;
}
.aon-x:hover {
  color: var(--ink);
  background: #eef2f0;
}
.lh-find {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
.search {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 8px 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #f7f9f8;
  color: var(--mut);
}
.search:focus-within {
  border-color: #9fcdb8;
  background: #fff;
}
.search :deep(.ic) {
  width: 16px;
  height: 16px;
}
.search input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  outline: none;
  padding: 0;
}
.search input::placeholder {
  color: #5e6d66;
}
.search input::-webkit-search-cancel-button {
  display: none;
}
.clr {
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
.clr:hover {
  background: #eef2f0;
}
.clr :deep(.ic) {
  width: 14px;
  height: 14px;
}
.dd-wrap {
  position: relative;
  flex: none;
}
.dd {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 8px 0 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  background: #fff;
}
.dd:hover {
  background: #f7f9f8;
}
.dd :deep(.ic) {
  width: 16px;
  height: 16px;
  color: var(--mut);
}
.dd-back {
  position: fixed;
  inset: 0;
  z-index: 20;
}
.dd-menu {
  position: absolute;
  right: 0;
  top: 40px;
  z-index: 21;
  min-width: 168px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(23, 33, 28, 0.12);
}
.dd-opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
}
.dd-opt:hover {
  background: #f4f6f5;
}
.dd-opt[aria-selected='true'] {
  color: var(--ac2);
  font-weight: 600;
}
.dd-opt :deep(.ic) {
  width: 15px;
  height: 15px;
}
.lh-opts {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}
.scope {
  display: inline-flex;
  padding: 2px;
  gap: 2px;
  background: #eef2f0;
  border-radius: 8px;
}
.sc-b {
  height: 26px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 26px;
  white-space: nowrap;
  color: #3e4d46;
}
.sc-b.is-on {
  background: #fff;
  color: var(--ink);
  font-weight: 600;
  box-shadow:
    0 1px 2px rgba(23, 33, 28, 0.08),
    0 0 0 1px rgba(23, 33, 28, 0.04);
}
/* AI tab: the filter does not apply (the tab is always the whole team) — dimmed, not clickable, the title says why */
.scope.is-off {
  background: #f3f5f4;
}
.scope.is-off .sc-b {
  cursor: help;
  color: #8a9993;
}
.scope.is-off .sc-b.is-on {
  color: #5d6b65;
  box-shadow: 0 0 0 1px rgba(23, 33, 28, 0.06);
}
.sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 4px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  color: var(--mut);
}
.sort:hover {
  background: #eef2f0;
  color: var(--ink);
}
.sort :deep(.ic) {
  width: 14px;
  height: 14px;
}
.tabs {
  display: flex;
  align-items: stretch;
  gap: 18px;
  height: 40px;
  margin-top: 8px;
}
.tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  color: #3e4d46;
}
.tab:hover {
  color: var(--ink);
}
.tab.is-on {
  color: var(--ink);
  font-weight: 600;
}
.tab.is-on::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: var(--ac);
}
.tab-b {
  flex: none;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--ac);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.tab-n {
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: var(--mut);
  font-variant-numeric: tabular-nums;
}
.tab :deep(.ic) {
  width: 13px;
  height: 13px;
}
.tab-ai {
  gap: 4px;
}
.tab-ai :deep(.ic) {
  color: var(--ac);
}
/* กำลังคุย: a small header per group — ต้องตอบ, then the quieter รอลูกค้าตอบ (sticks while its rows scroll) */
.grp {
  position: sticky;
  top: 0;
  z-index: 1;
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 16px;
  background: #f7f9f8;
  border-bottom: 1px solid var(--line2);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #2e3b35;
}
.grp-waiting {
  color: var(--mut);
  font-weight: 500;
  border-top: 1px solid var(--line2);
}
.grp-n {
  font-weight: 400;
  color: var(--mut);
  font-variant-numeric: tabular-nums;
}
.grp-done {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  color: var(--ac2);
}
.grp-done :deep(.ic) {
  width: 13px;
  height: 13px;
  stroke-width: 2.4;
}
/* AI tab: the row + its "รับเรื่องเอง" button over the right end of the row's AI line */
.ai-rw {
  position: relative;
  flex: none;
}
.take {
  position: absolute;
  right: 16px;
  bottom: 11px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px 0 8px;
  border: 1px solid #b9dccb;
  border-radius: 8px;
  background: #fff;
  color: var(--ac2);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.take:hover:not(:disabled) {
  background: var(--acs);
}
.take:disabled {
  opacity: 0.55;
}
.take :deep(.ic) {
  width: 13px;
  height: 13px;
}
.rows {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.rows-note {
  padding: 10px 16px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  border-bottom: 1px solid var(--line2);
}
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 48px 24px;
  color: var(--mut);
  text-align: center;
  font-size: 13px;
  line-height: 20px;
}
.empty :deep(.ic) {
  width: 28px;
  height: 28px;
  stroke-width: 1.5;
  color: #9fb3a9;
}
/* AI tab with nothing in it (usual while the owner keeps the AI mode off): calm, one sentence + where to switch it */
.ai-empty {
  gap: 6px;
}
.ai-em-ic {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: 4px;
  border-radius: 50%;
  background: var(--acs);
}
.ai-em-ic :deep(.ic) {
  width: 22px;
  height: 22px;
  color: var(--ac);
}
.em-t {
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: var(--ink);
}
.em-s {
  max-width: 260px;
  font-size: 13px;
  line-height: 20px;
  color: var(--mut);
}
.em-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--ac2);
  text-decoration: none;
}
.em-link:hover {
  text-decoration: underline;
}
.em-link :deep(.ic) {
  width: 14px;
  height: 14px;
  stroke-width: 2;
  color: var(--ac2);
}
</style>
